/*
 * @providesModule GenericHeader
 *
 * Reusable header with transparent background. Left icon, right icon, and center content
 * are optional and driven by props; both can be made functional via callbacks.
 */

import React, {useCallback, useState} from 'react';
import {TouchableOpacity, View} from 'react-native';
import SkillzStyle from 'SkillzStyle';
import SkillzSVG from 'SkillzSVG';
import {createStyles} from 'GenericHeaderStyle';
import HelpModal from 'HelpModal';

export interface GenericHeaderProps {
  onLeftIconPress?: () => void;
  onRightIconPress?: () => void;
  leftIconSource?: string;
  showLeftIcon?: boolean;
  showSkillzLogo?: boolean;
  showRightIcon?: boolean;
  rightIconSource?: string;
  hasBothSignInMethods?: boolean;
  variant?: 'sign-in' | 'sign-up';
  signInTab?: 'email' | 'phone';
}

const styles = createStyles();

export default function GenericHeader({
  onLeftIconPress,
  onRightIconPress,
  leftIconSource,
  showLeftIcon = true,
  showSkillzLogo = true,
  showRightIcon = true,
  rightIconSource,
  hasBothSignInMethods = false,
  variant = 'sign-in',
  signInTab = 'phone',
}: GenericHeaderProps) {
  const {textColorPrimary} = SkillzStyle.fullStyles;
  const iconTint = textColorPrimary?.color ?? '#FFFFFF';

  const {statusBarContentStyle} = SkillzStyle.fullStyles;
  const isDarkTheme = statusBarContentStyle === 0;

  const isLeftIconSvg = isDarkTheme ? 'arrow_left.svg' : 'arrow-left_black.svg';
  const skillzLogo = isDarkTheme ? 'skillz_dark_theme_icon.svg' : 'skillz_light_theme_icon.svg';
  const isRightIconSvg = isDarkTheme ? 'help_circle.svg' : 'help-circle_black.svg';

  const [showInfoModal, setShowInfoModal] = useState(false);

  const handleRightIcon = () => {
    setShowInfoModal(true);
  };

  const touchableIcons = useCallback(
    (iconSource: string, onIconPress: () => void) => (
      <TouchableOpacity
        style={styles.iconTouchable}
        onPress={onIconPress}
        hitSlop={{top: 6, left: 14, bottom: 6, right: 60}}
        accessible={true}
        accessibilityRole='button'
        accessibilityLabel='Back'
      >
        <SkillzSVG
          source={iconSource}
          style={styles.leftIcon}
          tintColor={iconTint}
          resizeMode='contain'
        />
      </TouchableOpacity>
    ),
    []
  );

  return (
    <View style={styles.container}>
      <View style={styles.leftIconSlot}>
        {showLeftIcon && touchableIcons(leftIconSource ?? isLeftIconSvg, () => onLeftIconPress?.())}
      </View>

      <View style={styles.centerSlot}>
        {showSkillzLogo && (
          <TouchableOpacity
            style={styles.centerTouchable}
            accessible={true}
            accessibilityRole='button'
            accessibilityLabel='Logo'
            hitSlop={{top: 6, left: 14, bottom: 6, right: 60}}
          >
            <SkillzSVG
              fill={SkillzStyle.getOverriddenFallbackTheme().textColorPrimary.color}
              source={skillzLogo}
              style={styles.centerIcon}
              resizeMode={'contain'}
            />
          </TouchableOpacity>
        )}
      </View>

      <View style={styles.rightIconSlot}>
        {showRightIcon &&
          touchableIcons(rightIconSource ?? isRightIconSvg, () =>
            (onRightIconPress ?? handleRightIcon)()
          )}
      </View>
      {showInfoModal && (
        <HelpModal
          visible={showInfoModal}
          onClose={() => setShowInfoModal(false)}
          hasBothSignInMethods={hasBothSignInMethods}
          variant={variant}
          signInTab={signInTab}
        />
      )}
    </View>
  );
}
